import { Button, TableCell, TableRow, useTheme } from '@material-ui/core'
import { Check } from '@material-ui/icons'
import React from 'react'
import { useHistory } from 'react-router-dom'
import {
    CellData,
    HeadCell,
    RenderRow,
    RowData,
    SimpleTable,
} from '../../components'
import { InspectionType } from '../../models'
import { useStyles } from '../../styles'
import { InspectionTypeCategory } from '../../models'
import { getUiCamelCase } from '../../helpers'
import { PREVIEW_INSPECTION_TYPE_STEP } from './InspectionConfiguration/InspectionConfigurationModal'
import { Routes } from '../../navigation/config/routes'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    selectedType?: InspectionType
    inspectionTypeList?: InspectionType[]
    setSelectedType: (type?: InspectionType) => void
    setModalOpen: (open: boolean, initialTab?: number) => void
    moveOutInspection: number | undefined
}

export const InspectionTypeTable = (props: Props) => {
    const {
        selectedType,
        setSelectedType,
        setModalOpen,
        inspectionTypeList,
        moveOutInspection,
    } = props

    const theme = useTheme()
    const classes = useStyles()
    const history = useHistory()

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
        padding: theme.spacing(2),
    }

    interface InspectionTypeRowType extends RowData {
        category: CellData<InspectionTypeCategory>
        name: CellData<string>
        numInventory: CellData<number>
        id: CellData<number>
    }

    const headCells: HeadCell<InspectionTypeRowType>[] = [
        {
            disablePadding: false,
            id: 'category',
            label: 'Category',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            id: 'name',
            label: 'Name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            id: 'numInventory',
            label: 'Inventory Items',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            id: 'numInventory',
            label: 'Move In Inspection',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            id: 'preview',
            label: '',
            style: headCellStyle,
        },
    ]

    const createRow = (insType: InspectionType): InspectionTypeRowType => {
        return {
            category: { value: insType.category, sortValue: insType.category },
            name: { value: insType.name, sortValue: insType.name },
            numInventory: {
                value: insType.inventory_configs.length,
                sortValue: insType.inventory_configs.length,
            },
            insType: { value: insType, sortValue: 1 },
            id: { value: insType.id, sortValue: 1 },
        }
    }

    interface InsTypeRowProps {
        row: InspectionTypeRowType
    }

    const InsTypeRow: RenderRow<InsTypeRowProps> = (props) => {
        const { row } = props
        const cellStyle: React.CSSProperties = {
            ...typographyStyle(theme.typography.body2),
            fontWeight: 400,
            padding: theme.spacing(1.5, 2),
        }

        return (
            <TableRow
                key={`INV_CONF_ROW_${row.insType.value.id}`}
                style={{
                    cursor: 'pointer',
                    backgroundColor:
                        selectedType?.id === row.insType.value.id
                            ? theme.palette.grey[300]
                            : undefined,
                }}
                className={classes.hoverGrey300}
                onClick={() => {
                    setSelectedType(row.insType.value)
                    history.push(
                        `${Routes.apartment.inspection.configureType}/${row.insType.value.id}`,
                    )
                }}
            >
                <TableCell style={{ ...cellStyle }}>
                    {getUiCamelCase(row.category.value)}
                </TableCell>
                <TableCell style={{ ...cellStyle }}>{row.name.value}</TableCell>
                <TableCell align="center" style={{ ...cellStyle }}>
                    {row.numInventory.value}
                </TableCell>
                <TableCell
                    align="center"
                    style={{ ...cellStyle, paddingRight: theme.spacing(2) }}
                >
                    {moveOutInspection === row.id.value ? (
                        <Check
                            style={{
                                color: theme.palette.primary.dark,
                                fontSize: '1.5rem',
                            }}
                        />
                    ) : (
                        ''
                    )}
                </TableCell>
                <TableCell
                    align="right"
                    style={{
                        ...cellStyle,
                        paddingRight: theme.spacing(3),
                        paddingLeft: theme.spacing(2),
                    }}
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        style={{
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.dark,
                            color: 'white',
                            padding: theme.spacing(0.75, 2),
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            borderRadius: theme.shape.borderRadius,
                            boxShadow: 'none',
                            transition: 'all 0.2s ease-in-out',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.primary.main
                            e.currentTarget.style.boxShadow = theme
                                .shadows[2] as string
                            e.currentTarget.style.transform = 'translateY(-1px)'
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.primary.dark
                            e.currentTarget.style.boxShadow = 'none'
                            e.currentTarget.style.transform = 'translateY(0)'
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                            setSelectedType(row.insType.value)
                            history.push(
                                `${Routes.apartment.inspection.configureType}/${row.insType.value.id}?tab=preview`,
                            )
                        }}
                    >
                        Preview/Edit
                    </Button>
                </TableCell>
            </TableRow>
        )
    }

    return (
        <SimpleTable<InspectionTypeRowType>
            rows={
                inspectionTypeList?.map((insType: InspectionType) =>
                    createRow(insType),
                ) ?? []
            }
            render={(row) => <InsTypeRow row={row} />}
            orderByDefault={'name'}
            headCells={headCells}
        />
    )
}
